/**
 * Renders hero overlays (text boxes + images) positioned absolutely.
 * Used by both the public HeroSection AND the admin preview canvas.
 */
import { resolveImg } from './imageUtils';

const Overlay = ({ overlay, onMouseDown, isSelected, editable }) => {
  const isCircle = overlay.type === 'text' && overlay.shape === 'circle';
  // Outer container — controls position, size, and aspect ratio (so circles stay round)
  const containerStyle = {
    position: 'absolute',
    left: `${overlay.x_percent ?? 50}%`,
    top: `${overlay.y_percent ?? 50}%`,
    width: `${overlay.width_percent ?? 20}%`,
    aspectRatio: isCircle ? '1 / 1' : undefined,
    transform: `translate(-50%, -50%) rotate(${overlay.rotation || 0}deg)`,
    cursor: editable ? 'move' : (overlay.link ? 'pointer' : 'default'),
    outline: editable ? (isSelected ? '2px solid #2a9d8f' : '1px dashed rgba(42,157,143,0.4)') : 'none',
    outlineOffset: '2px',
    zIndex: 20,
    userSelect: editable ? 'none' : 'auto',
  };

  // When editable, render plain divs (no link navigation while dragging).
  // When public, wrap in <a> if the overlay has a link.
  const Wrapper = (!editable && overlay.link)
    ? ({ children }) => (
        <a
          href={overlay.link}
          style={containerStyle}
          data-overlay-id={overlay.id}
          target={overlay.link.startsWith('http') ? '_blank' : undefined}
          rel={overlay.link.startsWith('http') ? 'noopener noreferrer' : undefined}
        >
          {children}
        </a>
      )
    : ({ children }) => (
        <div style={containerStyle} onMouseDown={onMouseDown} data-overlay-id={overlay.id}>
          {children}
        </div>
      );

  if (overlay.type === 'image') {
    return (
      <Wrapper>
        <img
          src={resolveImg(overlay.content || '')}
          alt=""
          className="w-full h-auto block pointer-events-none"
          style={{ borderRadius: `${overlay.border_radius || 0}px` }}
          draggable={false}
        />
      </Wrapper>
    );
  }

  // text — inner fills the container; font-size scales with width using clamp()
  const baseFontPx = Number(overlay.font_size) || 18;
  const innerStyle = {
    width: '100%',
    height: isCircle ? '100%' : 'auto',
    backgroundColor: overlay.bg_color || '#ff6b35',
    color: overlay.text_color || '#ffffff',
    fontSize: `clamp(10px, ${(baseFontPx / 16).toFixed(2)}cqw + 6px, ${baseFontPx}px)`,
    fontWeight: overlay.font_weight || 700,
    padding: `${overlay.padding_y ?? 12}px ${overlay.padding_x ?? 16}px`,
    borderRadius: isCircle ? '50%' : `${overlay.border_radius ?? 12}px`,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1.1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflow: 'hidden',
    boxSizing: 'border-box',
    pointerEvents: editable ? 'none' : 'auto',
    containerType: 'inline-size',
  };
  return (
    <Wrapper>
      <div style={innerStyle}>{overlay.content || ''}</div>
    </Wrapper>
  );
};

const HeroOverlays = ({ overlays = [], editable = false, selectedId, onSelect, onPositionChange }) => {
  const handleMouseDown = (e, overlay) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    if (onSelect) onSelect(overlay.id);
    if (!onPositionChange) return;

    const container = e.currentTarget.parentElement; // <section>
    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startXp = overlay.x_percent ?? 50;
    const startYp = overlay.y_percent ?? 50;

    const onMove = (ev) => {
      const dxPercent = ((ev.clientX - startX) / rect.width) * 100;
      const dyPercent = ((ev.clientY - startY) / rect.height) * 100;
      const newX = Math.max(0, Math.min(100, startXp + dxPercent));
      const newY = Math.max(0, Math.min(100, startYp + dyPercent));
      onPositionChange(overlay.id, newX, newY);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <>
      {overlays.map((ov) => (
        <Overlay
          key={ov.id}
          overlay={ov}
          isSelected={selectedId === ov.id}
          editable={editable}
          onMouseDown={(e) => handleMouseDown(e, ov)}
        />
      ))}
    </>
  );
};

export default HeroOverlays;
